<script lang="ts">
	import type { PublishedNews } from "@core/schema/News.js";
	import { getNewsDetailPath } from "@core/schema/paths/getNewsDetailPath.js";
	import { DateTime } from "@core/utils/luxon.js";
	import Link from "./Link.svelte";
	import Media from "./Media.svelte";

	const {
		article,
		compact = false,
	}: {
		article: PublishedNews;
		compact?: boolean;
	} = $props();

	const formattedDate = $derived(DateTime.fromISO(article.publishedAt).toFormat("d. M. yyyy"));
	const articleUrl = $derived(getNewsDetailPath(article));
</script>

<article
	class="group flex flex-col bg-white border border-ivory-300 rounded-2xl md:rounded-3xl overflow-hidden hover:shadow-lg transition-shadow duration-300"
	class:compact
>
	{#if article.primaryImage}
		<Link href={articleUrl} class="block overflow-hidden aspect-[16/10] relative">
			<Media
				source={article.primaryImage}
				alt={article.title}
				width={compact ? 400 : 800}
				height={compact ? 250 : 500}
				sourcesets={{}}
				class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
			/>
		</Link>
	{:else}
		<div class="aspect-[16/10] bg-gradient-to-br from-hazelnut-200 to-hazelnut-300 flex items-center justify-center">
			<span class="text-4xl md:text-5xl font-bold text-hazelnut-500/30">
				{article.title.charAt(0).toUpperCase()}
			</span>
		</div>
	{/if}

	<div class="flex flex-col p-4 md:p-6 gap-3 md:gap-4 flex-1">
		<div class="flex flex-col gap-2">
			<time datetime={article.publishedAt} class="text-sm text-hazelnut-500 font-medium">
				{formattedDate}
			</time>

			<Link href={articleUrl}>
				<h3
					class="text-base md:text-[21px] font-bold text-[#262626] leading-tight group-hover:text-hazelnut-600 transition-colors duration-200"
					class:line-clamp-2={!compact}
					class:line-clamp-3={compact}
				>
					{article.title}
				</h3>
			</Link>
		</div>

		{#if article.perex}
			<p
				class="text-sm md:text-base text-[#7D7D7D] leading-[1.5] font-light"
				class:line-clamp-3={!compact}
				class:line-clamp-2={compact}
			>
				{article.perex}
			</p>
		{/if}

		<Link
			href={articleUrl}
			class="mt-auto text-sm font-medium text-hazelnut-600 hover:text-hazelnut-700 inline-flex items-center gap-2 group/link"
		>
			Číst více
			<svg
				class="w-4 h-4 group-hover/link:translate-x-1 transition-transform duration-200"
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24"
			>
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
			</svg>
		</Link>
	</div>
</article>
